import { useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Page,
  Card,
  BlockStack,
  InlineStack,
  TextField,
  Button,
  Badge,
  DataTable,
  Text,
  Box,
  Banner,
  SkeletonBodyText,
  Modal,
  Toast,
  Frame,
} from '@shopify/polaris';
import { assetsApi } from '@/services';
import type { ReceiveLookupResult } from '@/services/assets';

const STATUS_BADGE: Record<
  string,
  { tone: 'success' | 'warning' | 'critical' | 'info' | undefined; label: string }
> = {
  ready: { tone: 'success', label: 'Ready' },
  allocated: { tone: 'info', label: 'Allocated' },
  in_use: { tone: 'info', label: 'In Use' },
  returned: { tone: 'warning', label: 'Returned' },
  qc_hold: { tone: 'warning', label: 'QC Hold' },
  repair: { tone: 'critical', label: 'Repair' },
  at_clinic: { tone: 'info', label: 'At Clinic' },
  retired: { tone: 'critical', label: 'Retired' },
};

export default function ReceiveRentalProduct() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // Filter field state
  const [serial, setSerial] = useState('');
  const [trackingNumber, setTrackingNumber] = useState('');
  const [orderNumber, setOrderNumber] = useState('');

  // The "committed" search params that trigger the query
  const [searchParams, setSearchParams] = useState<{
    serial?: string;
    trackingNumber?: string;
    orderNumber?: string;
  } | null>(null);

  // Ref for refocusing the serial field after each scan
  const serialInputRef = useRef<HTMLInputElement | null>(null);

  // Report-issue modal state
  const [reportModalAssetId, setReportModalAssetId] = useState<string | null>(null);
  const [issueNotes, setIssueNotes] = useState('');

  // Toast state
  const [toastMessage, setToastMessage] = useState<string | null>(null);
  const [toastIsError, setToastIsError] = useState(false);

  // ── Query ──────────────────────────────────────────────────────────────
  const hasSearch =
    searchParams != null &&
    (searchParams.serial || searchParams.trackingNumber || searchParams.orderNumber);

  const { data: results, isLoading } = useQuery({
    queryKey: ['receive-lookup', searchParams],
    queryFn: () => assetsApi.receiveLookup(searchParams!),
    enabled: !!hasSearch,
    retry: false,
  });

  // ── Mutations ──────────────────────────────────────────────────────────
  const markReceivedMutation = useMutation({
    mutationFn: ({ id, notes }: { id: string; notes?: string }) =>
      assetsApi.markReceived(id, { notes }),
    onSuccess: (_, vars) => {
      queryClient.invalidateQueries({ queryKey: ['receive-lookup', searchParams] });
      setToastIsError(false);
      setToastMessage(`Asset marked as received → QC Hold`);
      // Refocus + clear the serial field so the scanner can do the next one
      setSerial('');
      setTimeout(() => serialInputRef.current?.focus(), 50);
      void vars;
    },
    onError: (err: any) => {
      setToastIsError(true);
      setToastMessage(err?.response?.data?.message ?? 'Failed to mark received');
    },
  });

  const reportIssueMutation = useMutation({
    mutationFn: ({ id, notes }: { id: string; notes: string }) =>
      assetsApi.reportIssue(id, { notes }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['receive-lookup', searchParams] });
      setToastIsError(false);
      setToastMessage('Issue reported — asset sent to Repair');
      setReportModalAssetId(null);
      setIssueNotes('');
    },
    onError: (err: any) => {
      setToastIsError(true);
      setToastMessage(err?.response?.data?.message ?? 'Failed to report issue');
    },
  });

  // ── Handlers ───────────────────────────────────────────────────────────
  const handleSearch = useCallback(() => {
    const trimmed = {
      serial: serial.trim() || undefined,
      trackingNumber: trackingNumber.trim() || undefined,
      orderNumber: orderNumber.trim() || undefined,
    };
    if (!trimmed.serial && !trimmed.trackingNumber && !trimmed.orderNumber) return;
    setSearchParams(trimmed);
  }, [serial, trackingNumber, orderNumber]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') handleSearch();
    },
    [handleSearch],
  );

  const openReportModal = (assetId: string) => {
    setReportModalAssetId(assetId);
    setIssueNotes('');
  };

  const closeReportModal = () => {
    setReportModalAssetId(null);
    setIssueNotes('');
  };

  const submitReportIssue = () => {
    if (!reportModalAssetId || !issueNotes.trim()) return;
    reportIssueMutation.mutate({ id: reportModalAssetId, notes: issueNotes.trim() });
  };

  // ── Table rows ─────────────────────────────────────────────────────────
  const rows = (results ?? []).map((item: ReceiveLookupResult) => {
    const statusCfg = STATUS_BADGE[item.assetStatus] ?? { tone: undefined, label: item.assetStatus };
    return [
      item.rental?.shopifyOrderNumber ?? '—',
      item.serialNumber,
      item.productName ?? item.productSku,
      <Badge key={`status-${item.id}`} tone={statusCfg.tone}>
        {statusCfg.label}
      </Badge>,
      item.rental?.shippingTrackingNumber ?? '—',
      item.rental?.returnTrackingNumber ?? '—',
      item.rental?.notes ?? '—',
      <InlineStack key={`actions-${item.id}`} gap="200">
        <Button
          variant="primary"
          size="slim"
          onClick={() => markReceivedMutation.mutate({ id: item.id })}
          loading={markReceivedMutation.isPending && markReceivedMutation.variables?.id === item.id}
          disabled={['qc_hold', 'ready', 'repair', 'retired'].includes(item.assetStatus)}
        >
          Mark Received
        </Button>
        <Button
          size="slim"
          tone="critical"
          onClick={() => openReportModal(item.id)}
          disabled={['ready', 'retired'].includes(item.assetStatus)}
        >
          Report Issue
        </Button>
      </InlineStack>,
    ];
  });

  // ── Toast markup ───────────────────────────────────────────────────────
  const toastMarkup = toastMessage ? (
    <Toast
      content={toastMessage}
      tone={toastIsError ? 'magic' : undefined}
      error={toastIsError}
      onDismiss={() => setToastMessage(null)}
      duration={4000}
    />
  ) : null;

  return (
    <Frame>
      <Page
        title="Receive Rental Product"
        backAction={{ content: 'Assets', onAction: () => navigate('/assets') }}
      >
        <BlockStack gap="400">
          {/* Filter card */}
          <Card>
            <BlockStack gap="300">
              <Text as="h2" variant="headingMd">
                Scan or Search
              </Text>
              <InlineStack gap="300" blockAlign="end" wrap>
                {/* Serial — autoFocus, scanner-ready */}
                <Box minWidth="220px">
                  <div
                    ref={(el) => {
                      // Capture the underlying input for programmatic focus
                      const input = el?.querySelector('input');
                      if (input) (serialInputRef as React.MutableRefObject<HTMLInputElement | null>).current = input;
                    }}
                    onKeyDown={handleKeyDown}
                  >
                    <TextField
                      label="Serial Number"
                      value={serial}
                      onChange={setSerial}
                      autoComplete="off"
                      placeholder="Scan serial label..."
                      autoFocus
                    />
                  </div>
                </Box>

                {/* Tracking Number */}
                <Box minWidth="220px">
                  <div onKeyDown={handleKeyDown}>
                    <TextField
                      label="Tracking Number (AWB)"
                      value={trackingNumber}
                      onChange={setTrackingNumber}
                      autoComplete="off"
                      placeholder="e.g. 1Z123..."
                    />
                  </div>
                </Box>

                {/* Order Number */}
                <Box minWidth="180px">
                  <div onKeyDown={handleKeyDown}>
                    <TextField
                      label="Order Number"
                      value={orderNumber}
                      onChange={setOrderNumber}
                      autoComplete="off"
                      placeholder="e.g. #1234"
                    />
                  </div>
                </Box>

                <Button variant="primary" onClick={handleSearch} loading={isLoading}>
                  Search
                </Button>
              </InlineStack>
            </BlockStack>
          </Card>

          {/* Empty state — no search yet */}
          {!hasSearch && (
            <Box padding="400">
              <Text as="p" tone="subdued" alignment="center">
                Scan a serial or enter a tracking / order number to begin.
              </Text>
            </Box>
          )}

          {/* Loading skeleton */}
          {isLoading && (
            <Card>
              <SkeletonBodyText lines={5} />
            </Card>
          )}

          {/* No results */}
          {hasSearch && !isLoading && results && results.length === 0 && (
            <Banner tone="warning">
              <p>No matching assets found for the given search criteria.</p>
            </Banner>
          )}

          {/* Results table */}
          {results && results.length > 0 && (
            <Card>
              <DataTable
                columnContentTypes={[
                  'text', 'text', 'text', 'text', 'text', 'text', 'text', 'text',
                ]}
                headings={[
                  'Order #',
                  'Serial',
                  'Product',
                  'Status',
                  'Ship Track',
                  'Return Track',
                  'Notes',
                  'Actions',
                ]}
                rows={rows}
              />
            </Card>
          )}
        </BlockStack>

        {/* Report Issue modal */}
        <Modal
          open={reportModalAssetId !== null}
          onClose={closeReportModal}
          title="Report Issue"
          primaryAction={{
            content: 'Submit',
            onAction: submitReportIssue,
            loading: reportIssueMutation.isPending,
            disabled: !issueNotes.trim(),
          }}
          secondaryActions={[
            {
              content: 'Cancel',
              onAction: closeReportModal,
            },
          ]}
        >
          <Modal.Section>
            <TextField
              label="Describe the issue"
              value={issueNotes}
              onChange={setIssueNotes}
              multiline={3}
              autoComplete="off"
              placeholder="e.g. Cracked housing, missing cable, patient reported motor noise..."
            />
          </Modal.Section>
        </Modal>

        {toastMarkup}
      </Page>
    </Frame>
  );
}
